<template>
  <div class="w-full flex justify-center gap-2">
    <!-- 左侧键位绑定 -->
    <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 p-4">
      <div class="h-6 shrink-0">
        <span class="text-black-white text-l-medium">{{ $t('keys.socd.title') }}</span>
      </div>
      <div class="flex-1 flex flex-col mt-9 items-center">
        <!-- W & S -->
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-6">
            <Button color="glass" shape="rect" active class="w-12 h-12">W</Button>
            <Icon icon="Function-Wireframe-HorizontalBind" class="w-6 h-6 text-black-white" />
            <Button color="glass" shape="rect" active class="w-12 h-12">S</Button>
          </div>
          <div>
            <Switch v-model="wnsToggler" size="lg" @update:model-value="handleChangeWnS" />
          </div>
        </div>
        <div class="flex justify-center items-center mt-3">
          <div class="flex items-center gap-6">
            <button
              type="button"
              class="w-12 flex justify-center"
              :disabled="!isWnSInteractive"
              @click="handleSelectWnS('W')"
            >
              <Icon
                :icon="
                  isWnSDisplaySelected('W') ? 'Switch-Fill-Selected' : 'Switch-Wireframe-Unselected'
                "
                :class="[
                  'w-5 h-5 transition-colors',
                  getSelectIconClass(isWnSInteractive, isWnSDisplaySelected('W')),
                ]"
              />
            </button>
            <div class="w-6" />
            <button
              type="button"
              class="w-12 flex justify-center"
              :disabled="!isWnSInteractive"
              @click="handleSelectWnS('S')"
            >
              <Icon
                :icon="
                  isWnSDisplaySelected('S') ? 'Switch-Fill-Selected' : 'Switch-Wireframe-Unselected'
                "
                :class="[
                  'w-5 h-5 transition-colors',
                  getSelectIconClass(isWnSInteractive, isWnSDisplaySelected('S')),
                ]"
              />
            </button>
            <div class="w-10" />
          </div>
        </div>

        <!-- A & D -->
        <div class="flex items-center gap-6 mt-4">
          <div class="flex items-center gap-6">
            <Button color="glass" shape="rect" active class="w-12 h-12">A</Button>
            <Icon icon="Function-Wireframe-HorizontalBind" class="w-6 h-6 text-black-white" />
            <Button color="glass" shape="rect" active class="w-12 h-12">D</Button>
          </div>
          <div>
            <Switch v-model="andToggler" size="lg" @update:model-value="handleChangeAnD" />
          </div>
        </div>
        <div class="flex justify-center items-center mt-3">
          <div class="flex items-center gap-6">
            <button
              type="button"
              class="w-12 flex justify-center"
              :disabled="!isAnDInteractive"
              @click="handleSelectAnD('A')"
            >
              <Icon
                :icon="
                  isAnDDisplaySelected('A') ? 'Switch-Fill-Selected' : 'Switch-Wireframe-Unselected'
                "
                :class="[
                  'w-5 h-5 transition-colors',
                  getSelectIconClass(isAnDInteractive, isAnDDisplaySelected('A')),
                ]"
              />
            </button>
            <div class="w-6" />
            <button
              type="button"
              class="w-12 flex justify-center"
              :disabled="!isAnDInteractive"
              @click="handleSelectAnD('D')"
            >
              <Icon
                :icon="
                  isAnDDisplaySelected('D') ? 'Switch-Fill-Selected' : 'Switch-Wireframe-Unselected'
                "
                :class="[
                  'w-5 h-5 transition-colors',
                  getSelectIconClass(isAnDInteractive, isAnDDisplaySelected('D')),
                ]"
              />
            </button>
            <div class="w-10" />
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧绑定设置 -->
    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4 min-h-fit">
      <div class="flex gap-2">
        <!-- 触发优先级选择 -->
        <div class="basis-2/5 flex flex-col gap-2">
          <div
            v-for="item in methodList"
            :key="item.subMode"
            :class="[
              'flex-1 flex items-center gap-1 px-2 py-1.5 bg-key rounded-4xl transition-all cursor-pointer group',
              {
                'bg-white': activeMethod === item.subMode,
                'bg-key!': isMethodDisabled && activeMethod === item.subMode,
              },
              { 'pointer-events-none': isMethodDisabled },
            ]"
            @click="handleChangeMethod(item.subMode)"
          >
            <Icon
              :icon="item.icon"
              :class="[
                'w-8 h-8 transition-colors',
                `${activeMethod === item.subMode ? 'text-brand-blue2' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
                { 'text-text-disabled!': isMethodDisabled },
              ]"
            />
            <div>
              <p
                :class="[
                  'text-m-medium transition-colors',
                  `${activeMethod === item.subMode ? 'text-brand-blue2' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
                  { 'text-text-disabled!': isMethodDisabled },
                ]"
              >
                {{ item.title }}
              </p>
              <p
                :class="[
                  'text-s-medium transition-colors',
                  `${activeMethod === item.subMode ? 'text-blue-dark-hover' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
                  { 'text-text-disabled!': isMethodDisabled },
                ]"
              >
                {{ item.excerpt }}
              </p>
            </div>
          </div>
        </div>

        <!-- 触发开关-->
        <div class="basis-3/5 flex flex-col">
          <div class="p-4 flex flex-col gap-1 h-20 shrink-0 bg-key rounded-4xl">
            <div class="flex flex-col justify-between h-12">
              <div class="flex items-center justify-between">
                <div class="flex items-center text-black-white">
                  <Icon icon="Technology-Wireframe-QuickTrigger" class="w-6 h-6" />
                  <span class="text-m-medium">{{ $t('keys.socd.quickTrigger') }}</span>
                </div>
                <div>
                  <Switch
                    v-model="rtEnabled"
                    :disabled="isMethodDisabled"
                    @update:model-value="handleChangeRTEnabled"
                  />
                </div>
              </div>
              <p class="text-text-glass-default text-s-medium">
                {{ $t('keys.socd.quickTriggerDescription') }}
              </p>
            </div>
          </div>
          <div class="p-4 mt-2 flex-1 flex flex-col gap-1 bg-key rounded-4xl">
            <div class="flex flex-col justify-between h-12">
              <div class="flex items-center justify-between">
                <div class="flex items-center text-black-white">
                  <Icon icon="Function-Wireframe-CurrentClick" class="w-6 h-6" />
                  <span class="text-m-medium">{{ $t('keys.socd.executePoint') }}</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <span class="text-text-glass-default text-s-medium">
                    {{ $t('keys.socd.bottomTrigger') }}
                  </span>
                  <Switch
                    v-model="triggerWhenBottomEnabled"
                    size="md"
                    :disabled="isMethodDisabled"
                    @update:model-value="handleChangeTriggerWhenBottomEnabled"
                  />
                </div>
              </div>
              <p class="text-text-glass-default text-s-medium">
                {{ $t('keys.socd.executePointDescription') }}
              </p>
            </div>
            <div class="mt-2 flex items-start gap-3 w-full h-13.5">
              <!-- Min label -->
              <span class="text-black-white text-s-medium shrink-0">{{ MIN }}</span>

              <div class="relative flex-1 flex items-start h-16">
                <!-- 左侧死区：锯齿箭头-->
                <div
                  class="shrink-0 flex items-center h-5 overflow-hidden"
                  :style="{ width: '12%' }"
                >
                  <DeadZonePattern direction="left" />
                </div>

                <!-- 中间真实 Slider -->
                <div class="relative flex-1 h-5 flex items-center">
                  <Slider
                    v-model="value"
                    :min="SLIDE_MIN"
                    :max="SLIDE_MAX"
                    :step="1"
                    :disabled="isMethodDisabled"
                    class="w-full"
                    track-class="bg-glass30"
                    range-class="bg-brand-blue1 disabled:bg-disabled"
                    thumb-class="bg-white"
                    @value-commit="handleSliderValueCommit"
                  />
                  <div
                    class="absolute top-full left-0 h-7.5 -translate-x-1/2 pointer-events-none text-center leading-none"
                    :style="{ left: bubbleLeft }"
                  >
                    <div
                      :class="[
                        'text-brand-blue1 text-l-medium',
                        { 'text-text-disabled!': isMethodDisabled },
                      ]"
                    >
                      {{ currentValue }}
                    </div>
                    <div
                      :class="[
                        'text-text-glass-default text-s-medium',
                        { 'text-text-disabled!': isMethodDisabled },
                      ]"
                    >
                      (~{{ mmValue }}mm)
                    </div>
                  </div>
                </div>

                <!-- 右侧死区：锯齿箭头-->
                <div
                  class="shrink-0 flex items-center h-5 overflow-hidden"
                  :style="{ width: '24%' }"
                >
                  <DeadZonePattern direction="right" />
                </div>
              </div>

              <!-- Max label -->
              <span class="text-black-white text-s-medium shrink-0">{{ MAX }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, ref, watch } from 'vue'
  import { Button, Icon, Slider, Switch } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import { KEYCODE_KEYWORDS_MAP } from '../utils/keyMapSettings'
  import { parseNumber } from '../utils/keyIdentity'
  import {
    KeySettingsSymbol,
    RestoreKeyMappingWithTargetSymbol,
    UpdateKeyMappingWithTargetSymbol,
  } from '../types'
  import DeadZonePattern from './DeadZonePattern.vue'

  import type { KeyConfig } from '@rapoo/element'
  import type { SOCD } from '@rapoo/device-sdk'
  import type {
    KeyChangePayload,
    KeySetting,
    RestoreKeyMappingWithTarget,
    UpdateKeyMappingWithTarget,
  } from '../types'

  const { t } = useLocaleStore()

  // SOCD 仅支持这 4 个固定按键，且按两两成组处理。
  type SOCDKeywords = 'W' | 'S' | 'A' | 'D'
  type SOCDPair = readonly [SOCDKeywords, SOCDKeywords]

  // UI 与下发逻辑都依赖这个结构（只使用横向索引 index）。
  interface SOCDItem {
    keywords: SOCDKeywords
    index: number
  }

  // 固定键位元数据：用于构建 targetKey 和 SOCD payload。
  // index 为布局键的横向索引（key 字段），W=2*22+2、S/A/D 同理。UI 层只使用 index。
  const SOCD_KEYS: Record<SOCDKeywords, SOCDItem> = {
    W: { keywords: 'W', index: 46 },
    S: { keywords: 'S', index: 68 },
    A: { keywords: 'A', index: 67 },
    D: { keywords: 'D', index: 69 },
  }

  const WNS_PAIR: SOCDPair = ['W', 'S']
  const AND_PAIR: SOCDPair = ['A', 'D']

  // 滑块显示区间（沿用当前页面交互设定）。
  const MIN = 0
  const MAX = 255
  const SLIDE_MIN = 22
  const SLIDE_MAX = 204
  const THUMB_SIZE = 12
  const DEFAULT_SLIDER_VALUE = 36
  const THRESHOLD_MM_MAX = 3.5

  // subMode 与 UI 选项一一对应：1 后覆盖、2 绝对优先触发、3 冲突。
  const methodList = computed(
    () =>
      [
        {
          icon: 'Technology-Wireframe-RearOverlay',
          title: t('keys.socd.methods.rearCover.title'),
          excerpt: t('keys.socd.methods.rearCover.excerpt'),
          subMode: 1,
        },
        {
          icon: 'Technology-Wireframe-AbsPriorityTrigger',
          title: t('keys.socd.methods.absolutePriority.title'),
          excerpt: t('keys.socd.methods.absolutePriority.excerpt'),
          subMode: 2,
        },
        {
          icon: 'Technology-Wireframe-KeyConflict',
          title: t('keys.socd.methods.conflict.title'),
          excerpt: t('keys.socd.methods.conflict.excerpt'),
          subMode: 3,
        },
      ] as const,
  )

  // 父级注入：读取当前 keySettings，并调用统一 update/restore 能力。
  const keySettings = inject(KeySettingsSymbol)
  const updateKeyMappingWithTarget = inject<UpdateKeyMappingWithTarget>(
    UpdateKeyMappingWithTargetSymbol,
  )
  const restoreKeyMappingWithTarget = inject<RestoreKeyMappingWithTarget>(
    RestoreKeyMappingWithTargetSymbol,
  )

  const wnsToggler = ref(false)
  const andToggler = ref(false)
  const rtEnabled = ref(false)
  const triggerWhenBottomEnabled = ref(false)
  const activeMethod = ref<number>(1)
  const radioWnSValue = ref<'W' | 'S'>('W')
  const radioAnDValue = ref<'A' | 'D'>('A')
  const value = ref([DEFAULT_SLIDER_VALUE])
  // 滑块数值（22-204区间）由数组模式驱动，这里取单值。
  const currentValue = computed(() => value.value[0] ?? SLIDE_MIN)

  // UI 显示 mm 值，实际下发 triggerThreshold（单位 0.01mm）。
  const mmValue = computed(() => ((currentValue.value / MAX) * THRESHOLD_MM_MAX).toFixed(2))
  const triggerThreshold = computed(() =>
    Math.round((currentValue.value / MAX) * THRESHOLD_MM_MAX * 100),
  )

  const sliderProgress = computed(() => {
    const ratio = (currentValue.value - SLIDE_MIN) / (SLIDE_MAX - SLIDE_MIN)
    return Math.min(1, Math.max(0, ratio))
  })

  const bubbleLeft = computed(() => {
    const progress = sliderProgress.value
    const offset = (0.5 - progress) * THUMB_SIZE
    return `calc(${progress * 100}% + ${offset}px)`
  })

  const isMethodDisabled = computed(() => !wnsToggler.value && !andToggler.value)

  // 根据设备返回的 subMode 反查 UI 选项，找不到时回退到默认模式。
  const getMethodBySubMode = (subMode: unknown) => {
    const parsed = Number.parseInt(String(subMode ?? ''), 10)
    return methodList.value.find((item) => item.subMode === parsed) ?? methodList.value[0]
  }

  const getMethodBySubModeValue = (subMode: number) => {
    return methodList.value.find((item) => item.subMode === subMode) ?? methodList.value[0]
  }

  const activeSubMode = computed(() => getMethodBySubModeValue(activeMethod.value).subMode)
  // 仅绝对优先模式需要启用左右“单选优先键”。
  const isAbsolutePriorityMode = computed(() => activeSubMode.value === 2)

  const isWnSInteractive = computed(() => wnsToggler.value && isAbsolutePriorityMode.value)
  const isAnDInteractive = computed(() => andToggler.value && isAbsolutePriorityMode.value)

  // 设备回显的阈值（0.01mm）转换为 slider 值用于 UI 展示。
  const parseSliderValueByThreshold = (threshold: unknown) => {
    const parsed =
      typeof threshold === 'number' ? threshold : Number.parseInt(String(threshold ?? ''), 10)

    if (Number.isNaN(parsed)) return DEFAULT_SLIDER_VALUE

    const slider = Math.round((parsed / (THRESHOLD_MM_MAX * 100)) * MAX)
    return Math.min(MAX, Math.max(MIN, slider))
  }

  // 读取某个按键当前的 SOCD 设置。
  const getSOCDSettingByKey = (settings: KeySetting[], keywords: SOCDKeywords) => {
    return settings.find((item) => item.keyType === 'SOCD' && item.keywords === keywords)
  }

  // 一对绑定键都存在 SOCD 才算这一对“已启用 SOCD”。
  const hasPairSOCDSetting = (settings: KeySetting[], pair: SOCDPair) => {
    return Boolean(getSOCDSettingByKey(settings, pair[0]) && getSOCDSettingByKey(settings, pair[1]))
  }

  // 从回显里推断“绝对优先键”是哪一颗（subMode!=2 时回退为 pair[0]）。
  const resolvePairPriority = (settings: KeySetting[], pair: SOCDPair) => {
    const firstSOCD = getSOCDSettingByKey(settings, pair[0])?.raw?.socd
    const secondSOCD = getSOCDSettingByKey(settings, pair[1])?.raw?.socd

    if (firstSOCD?.subMode !== 2 && secondSOCD?.subMode !== 2) return pair[0]
    if (firstSOCD?.selfPriority === 1) return pair[0]
    if (secondSOCD?.selfPriority === 1) return pair[1]
    return pair[0]
  }

  // 用设备当前设置同步整个 SOCD 面板状态（开关、模式、阈值、优先键）。
  const syncUIBySettings = (settings: KeySetting[]) => {
    const hasWnS = hasPairSOCDSetting(settings, WNS_PAIR)
    const hasAnD = hasPairSOCDSetting(settings, AND_PAIR)

    wnsToggler.value = hasWnS
    andToggler.value = hasAnD

    if (!hasWnS && !hasAnD) {
      rtEnabled.value = false
      triggerWhenBottomEnabled.value = false
      activeMethod.value = 1
      value.value = [DEFAULT_SLIDER_VALUE]
      radioWnSValue.value = 'W'
      radioAnDValue.value = 'A'
      return
    }

    if (hasWnS) {
      radioWnSValue.value = resolvePairPriority(settings, WNS_PAIR) as 'W' | 'S'
    } else {
      radioWnSValue.value = 'W'
    }

    if (hasAnD) {
      radioAnDValue.value = resolvePairPriority(settings, AND_PAIR) as 'A' | 'D'
    } else {
      radioAnDValue.value = 'A'
    }

    const activePair = hasWnS ? WNS_PAIR : AND_PAIR
    const activeSetting =
      getSOCDSettingByKey(settings, activePair[0]) ?? getSOCDSettingByKey(settings, activePair[1])
    const activeSOCD = activeSetting?.raw?.socd

    rtEnabled.value = activeSOCD?.combineRT === 1
    triggerWhenBottomEnabled.value = activeSOCD?.triggerWhenBottom === 1
    activeMethod.value = getMethodBySubMode(activeSOCD?.subMode).subMode
    value.value = [parseSliderValueByThreshold(activeSOCD?.triggerThreshold)]
  }

  // 监听 keySettings，保证切层/改键后 UI 始终与设备状态一致。
  watch(
    () => keySettings?.value,
    (settings) => {
      syncUIBySettings((settings ?? []) as KeySetting[])
    },
    { immediate: true, deep: true },
  )

  // 组装 update/restore 使用的目标键（携带 index 以便精准下发）。
  const buildTargetKey = (item: SOCDItem) => {
    const config = KEYCODE_KEYWORDS_MAP.get(item.keywords)

    if (KEYCODE_KEYWORDS_MAP.has(item.keywords)) {
      return {
        ...config,
        key: item.index,
      }
    }

    return null
  }

  // selfPriority 规则：仅 subMode=2 生效，被选中的键传 1，否则传 0。
  const getSelfPriorityForKey = (keywords: SOCDKeywords) => {
    if (activeSubMode.value !== 2) return 0

    if (keywords === 'W' || keywords === 'S') {
      return radioWnSValue.value === keywords ? 1 : 0
    }

    return radioAnDValue.value === keywords ? 1 : 0
  }

  // 给单个按键下发 SOCD；一个 pair 会调用两次（互为 relatedKey）。
  const setSOCDForKey = (triggerKey: SOCDItem, relatedKey: SOCDItem) => {
    if (!updateKeyMappingWithTarget) return

    const targetKey = buildTargetKey(triggerKey)

    const socd: SOCD = {
      combineRT: rtEnabled.value ? 1 : 0,
      relatedKeyIndex: relatedKey.index,
      triggerKeyType: 0x00,
      triggerKeyCode: parseNumber(targetKey?.code) || 0,
      triggerKeyExtCode: 0x00,
      triggerThreshold: triggerThreshold.value,
      subMode: activeSubMode.value,
      triggerWhenBottom: triggerWhenBottomEnabled.value ? 1 : 0,
      selfPriority: getSelfPriorityForKey(triggerKey.keywords),
    }

    const payload: KeyChangePayload = {
      keywords: triggerKey.keywords,
      keyType: 'SOCD',
      target: null,
      raw: {
        type: 0x0e,
        socd,
      },
    }

    updateKeyMappingWithTarget(payload, targetKey as KeyConfig)
  }

  // 下发一对键：必须双键都 setKey，保证设备配置成对生效。
  const applyPair = (pair: SOCDPair) => {
    const first = SOCD_KEYS[pair[0]]
    const second = SOCD_KEYS[pair[1]]

    setSOCDForKey(first, second)
    setSOCDForKey(second, first)
  }

  // 恢复某个 SOCD 键到默认映射。
  const restoreKeyByCode = (keyCode: SOCDKeywords) => {
    if (!restoreKeyMappingWithTarget) return
    restoreKeyMappingWithTarget(buildTargetKey(SOCD_KEYS[keyCode]) as KeyConfig)
  }

  // 恢复一对 SOCD 键。
  const restorePair = (pair: SOCDPair) => {
    restoreKeyByCode(pair[0])
    restoreKeyByCode(pair[1])
  }

  // 右侧表单变化时，对当前所有已开启的 pair 重新下发配置。
  const applyCurrentActivePairs = () => {
    if (wnsToggler.value) {
      applyPair(WNS_PAIR)
    }

    if (andToggler.value) {
      applyPair(AND_PAIR)
    }
  }

  // 切换 subMode：仅在面板可用时允许切换，并立即对已启用 pair 生效。
  const handleChangeMethod = (nextSubMode: number) => {
    if (isMethodDisabled.value) return

    activeMethod.value = getMethodBySubModeValue(nextSubMode).subMode
    applyCurrentActivePairs()
  }

  // W/S 开关：关则恢复，开则仅下发 W/S（不影响 A/D）。
  const handleChangeWnS = (nextValue: boolean) => {
    wnsToggler.value = nextValue

    if (!nextValue) {
      restorePair(WNS_PAIR)
      return
    }

    applyPair(WNS_PAIR)
  }

  // A/D 开关：关则恢复，开则仅下发 A/D（不影响 W/S）。
  const handleChangeAnD = (nextValue: boolean) => {
    andToggler.value = nextValue

    if (!nextValue) {
      restorePair(AND_PAIR)
      return
    }

    applyPair(AND_PAIR)
  }

  // combineRT 切换后，所有已激活 pair 立即重下发。
  const handleChangeRTEnabled = (nextValue: boolean) => {
    rtEnabled.value = nextValue
    applyCurrentActivePairs()
  }

  // triggerWhenBottom 切换后，所有已激活 pair 立即重下发。
  const handleChangeTriggerWhenBottomEnabled = (nextValue: boolean) => {
    triggerWhenBottomEnabled.value = nextValue
    applyCurrentActivePairs()
  }

  // 滑块提交时再下发，避免拖动过程频繁发送。
  const handleSliderValueCommit = (nextValue: number[] | undefined) => {
    if (nextValue?.length) {
      value.value = nextValue
    }
    applyCurrentActivePairs()
  }

  // 单选图标颜色规则（启用/禁用 + 选中/未选中）。
  const getSelectIconClass = (enabled: boolean, selected: boolean) => {
    if (selected && enabled) return 'text-brand-blue1'
    if (selected && !enabled) return 'text-text-disabled'
    return enabled ? 'text-text-body-default' : 'text-text-disabled'
  }

  // 非绝对优先模式时，两个点位都显示为“选中”态（仅表现态，不改 payload）。
  const isWnSDisplaySelected = (key: 'W' | 'S') => {
    const forceDualSelected = !isWnSInteractive.value
    return forceDualSelected || radioWnSValue.value === key
  }

  const isAnDDisplaySelected = (key: 'A' | 'D') => {
    const forceDualSelected = !isAnDInteractive.value
    return forceDualSelected || radioAnDValue.value === key
  }

  // 选择 W/S 优先键：仅在绝对优先模式 + W/S 已开启时可操作。
  const handleSelectWnS = (nextValue: 'W' | 'S') => {
    if (!isWnSInteractive.value) return
    if (!wnsToggler.value) return

    radioWnSValue.value = nextValue
    applyPair(WNS_PAIR)
  }

  // 选择 A/D 优先键：仅在绝对优先模式 + A/D 已开启时可操作。
  const handleSelectAnD = (nextValue: 'A' | 'D') => {
    if (!isAnDInteractive.value) return
    if (!andToggler.value) return

    radioAnDValue.value = nextValue
    applyPair(AND_PAIR)
  }
</script>

<style scoped></style>
